import { SESSION_KICKED_CODE } from "@/lib/auth/session-kicked";
import {
  parseAuthScope,
  resolveAuthScopeFromPathname,
  type AuthScope,
} from "@/lib/auth/auth-scope";

let refreshPromise: Promise<AuthRefreshResult> | null = null;

export type AuthRefreshResult =
  | { ok: true; phoneRequired?: boolean }
  | { ok: false; code?: string; message?: string; retryable?: boolean };

function resolveAuthScope(scope?: AuthScope): AuthScope {
  if (scope) {
    return scope;
  }

  if (typeof window !== "undefined") {
    const fromPath = resolveAuthScopeFromPathname(window.location.pathname);
    if (fromPath) {
      return fromPath;
    }
  }

  return "member";
}

async function parseAuthResponse(response: Response): Promise<AuthRefreshResult> {
  try {
    const body = (await response.json()) as {
      ok?: boolean;
      data?: { phoneRequired?: boolean };
      error?: { code?: string; message?: string };
    };

    if (response.ok && body.ok === true) {
      return { ok: true, ...(body.data?.phoneRequired ? { phoneRequired: true } : {}) };
    }

    const authFailure =
      [200, 401, 403].includes(response.status) &&
      body.ok === false &&
      ["UNAUTHORIZED", "INVALID_REFRESH_TOKEN", "PHONE_BINDING_REQUIRED", SESSION_KICKED_CODE].includes(
        body.error?.code ?? "",
      );

    return {
      ok: false,
      code: body.error?.code,
      message: body.error?.message,
      retryable: !authFailure,
    };
  } catch {
    return { ok: false, retryable: true };
  }
}

export async function refreshAuthSession(
  scope = resolveAuthScope(),
): Promise<AuthRefreshResult> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const response = await fetch("/api/auth/refresh", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ scope }),
      });

      return parseAuthResponse(response);
    } catch {
      return { ok: false, retryable: true };
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export async function probeAuthSession(
  scope = resolveAuthScope(),
): Promise<AuthRefreshResult> {
  try {
    const response = await fetch(`/api/auth/me?scope=${scope}`, {
      credentials: "include",
    });

    return parseAuthResponse(response);
  } catch {
    return { ok: false, retryable: true };
  }
}

export function isSessionKicked(result: AuthRefreshResult) {
  return !result.ok && result.code === SESSION_KICKED_CODE;
}

export async function ensureAuthSession(
  scope = resolveAuthScope(),
): Promise<AuthRefreshResult> {
  const probe = await probeAuthSession(scope);

  if (probe.ok || probe.retryable || isSessionKicked(probe) || probe.code === "PHONE_BINDING_REQUIRED") {
    return probe;
  }

  return refreshAuthSession(scope);
}

export const MEMBER_SESSION_CHANGED_EVENT = "member-session-changed";

export async function logoutAuthSession(scope = resolveAuthScope()) {
  await fetch("/api/auth/logout", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ scope }),
  });
  if (scope === "member" && typeof window !== "undefined") window.dispatchEvent(new Event(MEMBER_SESSION_CHANGED_EVENT));
}

export function resolveScopeFromSearchParam(value: string | null): AuthScope {
  return parseAuthScope(value) ?? "member";
}
